import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { language, setLanguage, t, direction } = useLanguage();
  const location = useLocation();

  const navItems = [
    { path: '/', label: t.nav.about },
    { path: '/services', label: t.nav.services },
    { 
      path: '/distribution', 
      label: t.nav.distribution,
      hasSubmenu: true,
      submenu: [
        { path: '/distribution/viya-crab', label: 'VIYA CRAB' },
        { path: '/distribution/golden-sheaf', label: 'GOLDEN SHEAF' }
      ]
    },
    { path: '/trading', label: t.nav.trading },
    { path: '/contact', label: t.nav.contact },
  ];

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ro', name: 'Română', flag: '🇷🇴' },
    { code: 'ar', name: 'العربية', flag: '🇦🇪' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/services';
    }
    return location.pathname === path;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center transition-opacity hover:opacity-80">
            <img
              src="https://customer-assets.emergentagent.com/job_aria-trading/artifacts/xwcdwjvr_Aria_color_Transparent.png"
              alt="Aria MEA"
              className="h-[92px] object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://customer-assets.emergentagent.com/job_flour-global-market/artifacts/e3nlk50l_WordMark_Aria_Logo_Green.png';
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              item.hasSubmenu ? (
                <DropdownMenu key={item.path}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        location.pathname.startsWith('/distribution')
                          ? 'text-[#007369] bg-[#F4F7F6]'
                          : 'text-gray-700 hover:text-[#007369] hover:bg-gray-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {item.submenu.map((subItem) => (
                      <DropdownMenuItem key={subItem.path} asChild>
                        <Link
                          to={subItem.path}
                          onClick={() => window.scrollTo(0, 0)}
                          className={`cursor-pointer ${
                            location.pathname === subItem.path ? 'bg-[#F4F7F6]' : ''
                          }`}
                        >
                          {subItem.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => window.scrollTo(0, 0)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive(item.path)
                      ? 'text-[#007369] bg-[#F4F7F6]'
                      : 'text-gray-700 hover:text-[#007369] hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </Link>
              )
            ))}
          </nav>

          {/* Language Switcher & Mobile Menu */}
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Globe className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {languages.find((l) => l.code === language)?.name}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={direction === 'rtl' ? 'start' : 'end'}>
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={language === lang.code ? 'bg-[#F4F7F6]' : ''}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-700 hover:text-[#007369] transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden py-4 border-t border-gray-100">
            {navItems.map((item) => (
              <div key={item.path}>
                {item.hasSubmenu ? (
                  <div>
                    <Link
                      to={item.path}
                      onClick={() => { window.scrollTo(0, 0); setIsMenuOpen(false); }}
                      className={`block px-4 py-3 text-sm font-medium rounded-lg transition-all ${
                        location.pathname.startsWith('/distribution')
                          ? 'text-[#007369] bg-[#F4F7F6]'
                          : 'text-gray-700 hover:text-[#007369] hover:bg-gray-50'
                      }`}
                    >
                      {item.label}
                    </Link>
                    <div className="pl-4">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => { window.scrollTo(0, 0); setIsMenuOpen(false); }}
                          className={`block px-4 py-2 text-xs font-medium rounded-lg transition-all ${
                            location.pathname === subItem.path
                              ? 'text-[#007369] bg-[#F4F7F6]'
                              : 'text-gray-600 hover:text-[#007369] hover:bg-gray-50'
                          }`}
                        >
                          → {subItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => { window.scrollTo(0, 0); setIsMenuOpen(false); }}
                    className={`block px-4 py-3 text-sm font-medium rounded-lg transition-all ${
                      isActive(item.path)
                        ? 'text-[#007369] bg-[#F4F7F6]'
                        : 'text-gray-700 hover:text-[#007369] hover:bg-gray-50'
                    }`}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;